import {Injectable} from '@angular/core';
import {WizardNestedFormService} from '../abstract/wizard-nested-form-service';
import {FormBuilder, Validators} from '@angular/forms';
import {WizardValidators} from '../../wizard-validators';
import {WizardEnums} from '../../../wizard.namespace';
import {WizardFormEventsService} from '../wizard-form-events.service';
import {distinctUntilChanged, map, takeUntil} from 'rxjs/operators';
import {LaboratoryDocumentsProvider} from '@providers/laboratory-documents/laboratory-documents.provider';
import {LaboratoryDocument} from '@models/laboratory-document';
import {Observable} from 'rxjs';
import {LaboratoryDocumentsNamespace} from '@components/laboratory-documents/laboratory-documents.namespace';

@Injectable()
export class LaboratoryCommonService extends WizardNestedFormService {
    form = this.fb.group({
        _touched: [false, Validators.requiredTrue],

        /**
         * Территориальный орган Ростехнадзора
         */
        rosteh_id: ['', WizardValidators.required()],

        /**
         * Фактический адрес лаборатории
         */
        address_type: [WizardEnums.LaboratoryRealAddressTypes.asOrganizationReal, WizardValidators.required()],

        /**
         * Указанный самостоятельно фактически адрес лаборатории
         * Индекс, Город, Адрес
         */
        address_city_id: ['', WizardValidators.required('Город')],
        address_city: ['', WizardValidators.required('Город')],
        address_index: ['', WizardValidators.index()],
        address: ['', WizardValidators.required('Адрес')],

        rooms: ['', WizardValidators.required('Количество комнат помещения лаборатории')],
        square: ['', WizardValidators.required('Общая площадь помещений лаборатории')],

        /**
         * Полное наименование
         */
        title: ['', [
            WizardValidators.required('Полное название лаборатории'),
            WizardValidators.withoutSymbols('названии лаборатории'),
        ]],

        /**
         * Сокращенное наименование
         */
        short_title: ['', [
            WizardValidators.required('Сокращенное название лаборатории'),
            WizardValidators.withoutSymbols('названии лаборатории'),
        ]],

        /**
         * Передвижная лаборатория?
         */
        is_mobile: [false],

        /**
         * Аттестовать лабораторию с методом РК?
         */
        with_rk: [false], // Аттестовать с РК?

        /**
         * Имеются ли нужные документы для аттестации с РК?
         */
        has_documents: [false, [WizardValidators.requiredTrue()]],
    });

    public documents$: Observable<LaboratoryDocument[]>;

    constructor(
        private fb: FormBuilder,
        private wizardFormEvents: WizardFormEventsService,
        private laboratoryDocumentsProvider: LaboratoryDocumentsProvider,
    ) {
        super();

        this.documents$ = this.laboratoryDocumentsProvider.data$.pipe(
            takeUntil(this.destroyed$),
            map((list: LaboratoryDocument[]) => list.filter(item => {
                return [
                    LaboratoryDocumentsNamespace.DocumentTypeEnum.sanitation,
                    LaboratoryDocumentsNamespace.DocumentTypeEnum.iiiLicense,
                ].includes(item.type);
            }))
        );
    }

    init() {
        this.form.get('with_rk').valueChanges.pipe(
            takeUntil(this.destroyed$),
            distinctUntilChanged(),
            map(v => Boolean(v)),
        ).subscribe((withRk) => {
            this.wizardFormEvents.setIsRk(withRk);

            if (withRk) {
                this.form.get('has_documents').enable();
            } else {
                this.form.get('has_documents').disable();
            }
        });

        this.form.get('with_rk').updateValueAndValidity();


        this.form.get('address_type').valueChanges.pipe(
            takeUntil(this.destroyed$),
            distinctUntilChanged(),
        ).subscribe((type) => {
            [
                'address_city_id',
                'address_city',
                'address_index',
                'address',
            ].forEach((name) => {
                if (+type === WizardEnums.LaboratoryRealAddressTypes.custom) {
                    this.form.get(name).enable();
                } else {
                    this.form.get(name).disable();
                }
            });
        });

        this.form.get('address_type').updateValueAndValidity();

        this.wizardFormEvents.isRk$.pipe(
            takeUntil(this.destroyed$),
        ).subscribe((isRk: boolean) => {
            this.form.get('with_rk').patchValue(isRk);
        });

        this.documents$.pipe(
            takeUntil(this.destroyed$)
        ).subscribe((list: LaboratoryDocument[]) => {
            const hasIii = list.some(item => item.type === LaboratoryDocumentsNamespace.DocumentTypeEnum.iiiLicense);
            const hasSanitation = list.some(item => item.type === LaboratoryDocumentsNamespace.DocumentTypeEnum.sanitation);

            this.form.get('has_documents').patchValue(Boolean(hasIii && hasSanitation));
        });
    }

    restoreValidators() {
        /**
         * Обновим статус выбора РК на тот, что сейчас в этом значении
         */
        const withRkValue = this.form.get('with_rk').value;
        this.wizardFormEvents.setIsRk(withRkValue);
    }
}
